/**
 * © 2025 Indikin.online. All rights reserved.
 * Made with ❤️ by YalaCoder.
 *
 * Analytics Service
 *
 * --- REWRITE SUMMARY (Beacon Implementation) ---
 * - NEW (sendBeacon function): Added a new `sendBeacon` function to reliably
 * send a final analytics event when the user navigates away from the page.
 * - This function uses `navigator.sendBeacon()`, the modern, non-blocking API
 * designed for this exact purpose. It replaces the need for deprecated `unload`
 * event listeners.
 * - It sends a JSON payload as a Blob to ensure the `Content-Type` header is
 * set correctly, which is a best practice.
 * - The function is designed to fail gracefully if the browser does not
 * support the Beacon API.
 */

/**
 * Tracks a page view in Google Analytics.
 * This is essential for single-page applications (SPAs) to log navigation events.
 * It should only be called after the GA script has been loaded and initialized.
 * @param {string} path - The path of the page to track (e.g., '/home').
 */
export const trackPageView = (path) => {
  const hasConsent = document.cookie.includes('indikin_cookie_consent=accepted');
  if (!hasConsent) return;

  if (typeof window.gtag !== 'function') {
    console.warn('Google Analytics not available. Page view not tracked.');
    return;
  }
  
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href
  });
  console.log(`Tracked page view for: ${path}`);
};


// --- Microsoft Clarity ---

/**
 * Initializes the Microsoft Clarity tracking script by injecting it into the document head.
 * @param {string} clarityId - Your Microsoft Clarity Project ID.
 * @param {string} nonce - The CSP nonce to apply to the script tag.
 */
export const initMicrosoftClarity = (clarityId, nonce) => {
  // Prevent re-initialization
  if (window.clarity || document.querySelector(`script[src*="${clarityId}"]`)) {
    return;
  }
  
  try {
    (function(c,l,r,i,t,y){
        c.clarity=c.clarity||function(){(c.clarity.q=c.clarity.q||[]).push(arguments)};
        t=l.createElement(r);
        t.async=1;
        t.src="https://www.clarity.ms/tag/"+i;
        
        // --- CSP Compliance ---
        // Apply the nonce to the script tag before injection.
        if (nonce) {
          t.nonce = nonce;
        }
        
        y=l.getElementsByTagName(r)[0];
        y.parentNode.insertBefore(t,y);
    })(window, document, "script", clarityId);
    console.log(`Microsoft Clarity initialized for ${clarityId}`);
  } catch (e) {
    console.error("Microsoft Clarity initialization failed.", e);
  }
};


// --- Beacon API for End-of-Session Tracking (NEW) ---

/**
 * Sends analytics data using the Beacon API.
 * This is the recommended way to send data when a page is unloading,
 * as it's non-blocking and more reliable than other methods.
 * @param {object} payload - The analytics data to send.
 * @returns {boolean} - True if the beacon was successfully queued, false otherwise.
 */
export const sendBeacon = (payload) => {
  const hasConsent = document.cookie.includes('indikin_cookie_consent=accepted');
  if (!hasConsent) return false;

  // Check if the Beacon API is supported by the browser.
  if (!navigator.sendBeacon) {
    console.warn('Beacon API not supported. End-of-session event not sent.');
    return false;
  }

  try {
    const beaconUrl = '/beacon'; // The endpoint we created in worker.js
    // Create a Blob with the correct Content-Type for the backend to parse.
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    
    // Queue the data for transmission. The browser will send it when it can.
    const success = navigator.sendBeacon(beaconUrl, blob);
    
    if (success) {
      console.log('Successfully queued end-of-session beacon:', payload);
    } else {
      console.error('Failed to queue end-of-session beacon.');
    }
    
    return success;
  } catch (error) {
    console.error('Error sending beacon:', error);
    return false;
  }
};

/**
 * Track a custom analytics event (consent-gated).
 * Only fires if the user has accepted cookie consent.
 * @param {string} eventName - GA4 event name (e.g., 'exit_intent_shown')
 * @param {Object} [params] - Optional event parameters
 */
export function trackEvent(eventName, params = {}) {
  // [v3 FIX] Correct cookie name: indikin_cookie_consent (not cookie_consent)
  const hasConsent = document.cookie.includes('indikin_cookie_consent=accepted');
  if (!hasConsent) return;

  // Google Analytics (gtag)
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }
}
